import { autoinject } from 'aurelia-framework';
import { json, xml } from 'vkbeautify';

import { Membership } from 'src/forms/ato-interfaces/models/membership';
import { APIParameters, GenericApi, ParamType } from '~/api/generic-api';
import { ATOInterfacesModel } from '~/forms/ato-interfaces/models/ato-interfaces-model';
import { RequestControlFilters } from '~/forms/ato-interfaces/models/request-control';
import { FetchUtils, HttpMethod } from '~/utils/fetch-utils';

@autoinject
export class AtoInterfacesApi extends GenericApi {
	private getFormURLPrefix(
		fund?: string,
		member?: string,
		division?: string,
	): string {
		let urlPrefix = GenericApi.apiServerURL + 'api';
		if (member && member.length > 0) {
			urlPrefix += '/member/' + fund + '/' + member;
		} else if (fund && fund.length > 0) {
			urlPrefix += '/fund/' + fund;
		} else if (division && division.length > 0) {
			urlPrefix += '/division/' + division;
		}
		urlPrefix += '/ato-interfaces';
		return urlPrefix;
	}

	public getATOInterfacesTableDataApiUrl(
		fund: string,
		member: string,
		division: string,
		queryParams?: Map<string, string>,
	) {
		return (
			this.getFormURLPrefix(fund, member, division) +
			'?' +
			GenericApi.getParamsString(
				{},
				[ParamType.FORM, ParamType.DIVISION],
				queryParams,
			)
		);
	}

	public getATOInterfacesData(
		member: string,
		fund: string,
		requestControlFilters?: RequestControlFilters,
	): Promise<any> {
		let url =
			this.getFormURLPrefix(fund, member) +
			'/ui-model?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		if (requestControlFilters) {
			url += '&' + $.param(requestControlFilters);
		}
		return FetchUtils.getJSONWithParamsInURL<any>(url).then(
			GenericApi.getFieldModel,
		);
	}

	public getAtoInterfacesRequestResponse(requestId: number): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			requestId +
			'?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.getJSONWithParamsInURL<any>(url)
			.then(GenericApi.getFieldModel)
			.then(AtoInterfacesApi.formatCodeBlocks);
	}

	public getCancellationRequestResponse(
		cancelRequestId: number,
	): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			cancelRequestId +
			'/cancellation?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.getJSONWithParamsInURL<any>(url)
			.then(GenericApi.getFieldModel)
			.then(AtoInterfacesApi.formatCodeBlocks);
	}

	private static formatCodeBlocks(
		model: ATOInterfacesModel,
	): ATOInterfacesModel {
		// responseBean may be null due to payroll security. No need to format code block if no responseBean
		if (!model.responseBean) {
			return model;
		}
		const request = model.responseBean.request;
		const response = model.responseBean.response;
		if (request && request.mimeType === 'application/json') {
			request.payload = json(request.payload, 2);
		}
		if (response && response.mimeType === 'application/json') {
			response.payload = json(response.payload, 2);
		}

		let rawEventItems = model.responseBean.rawEventItems;
		if (rawEventItems && rawEventItems.trim() !== '') {
			rawEventItems = xml(rawEventItems, 2);
		}

		return model;
	}

	public requestNewStic3(member: string, fund: string): Promise<any> {
		const url =
			this.getFormURLPrefix(fund, member) +
			'/stic3/validation?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public requestRegistrationStic3(
		member: string,
		fund: string,
	): Promise<any> {
		const url =
			this.getFormURLPrefix(fund, member) +
			'/stic3/registration?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public requestClosedAccountStic3(
		member: string,
		fund: string,
	): Promise<any> {
		const url =
			this.getFormURLPrefix(fund, member) +
			'/stic3/closed-account?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public requestNewSmat2(member: string, fund: string): Promise<any> {
		const url =
			this.getFormURLPrefix(fund, member) +
			'/smat2?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public getSmat2RolloverMessages(
		clientID: string,
		fundAbnValue: string,
		memberUsiValue: string,
	): Promise<any> {
		const params: APIParameters = {
			fundAbn: fundAbnValue,
			memberUsi: memberUsiValue,
		};
		const url =
			GenericApi.apiServerURL +
			'api/client/' +
			clientID +
			'/ato-interfaces/rollover-messages?' +
			GenericApi.getParamsString(params, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.getJSONWithParamsInURL<any>(url);
	}

	public requestMaasGet(
		member: string,
		fund: string,
		reasonValue: string,
	): Promise<any> {
		const params: APIParameters = {
			reason: reasonValue,
		};
		const url =
			this.getFormURLPrefix(fund, member) +
			'/maas-get?' +
			GenericApi.getParamsString(params, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public requestMaasSubmitRegistration(
		member: string,
		fund: string,
	): Promise<any> {
		const url =
			this.getFormURLPrefix(fund, member) +
			'/maas-submit-registration?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public requestMaasSubmitUpdate(member: string, fund: string): Promise<any> {
		const url =
			this.getFormURLPrefix(fund, member) +
			'/maas-submit-update?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.POST, url);
	}

	public releaseFilteredOnHoldRequests(
		fund: string,
		member: string,
		division: string,
		tableParams: APIParameters,
	): Promise<any> {
		const params: APIParameters = {
			...tableParams,
		};
		const url =
			this.getFormURLPrefix(fund, member, division) +
			'/release-filtered-on-hold?' +
			GenericApi.getParamsString(params, [
				ParamType.FORM,
				ParamType.DIVISION,
				ParamType.CUSTOMISATION,
			]);

		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.PUT, url);
	}

	public releaseOneOnHoldRequest(requestId: number): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			requestId +
			'/release-on-hold?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.PUT, url);
	}

	public updateSelectedData(
		requestId: number,
		rowsToUpdate: string[],
		resumeCorrespondence: boolean,
	): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			requestId +
			'/update-selected-data/' +
			rowsToUpdate +
			'/' +
			resumeCorrespondence +
			'?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.PUT, url);
	}

	public markResponseAsReviewed(requestId: number): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			requestId +
			'/mark-response-as-reviewed?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.PUT, url);
	}

	public resubmitRequest(requestId: number): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			requestId +
			'/resubmit-request?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.PUT, url);
	}

	/**
	 * Batch ID needs to be sent as a string as some precision is
	 * lost when converting strings to long numbers in Javascript
	 */
	public resubmitBatch(batchId: string): Promise<any> {
		const url =
			this.getFormURLPrefix() +
			'/' +
			batchId +
			'/resubmit-batch?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.fetchJSONWithParamsInURL<any>(HttpMethod.PUT, url);
	}

	public getHaltCorrespondence(membership: Membership): Promise<any> {
		const url =
			GenericApi.apiServerURL +
			'api/member/' +
			membership.fund +
			'/' +
			membership.member +
			'/halt-correspondence/' +
			'?' +
			GenericApi.getParamsString({}, [
				ParamType.FORM,
				ParamType.DIVISION,
			]);
		return FetchUtils.getJSONWithParamsInURL<any>(url);
	}

	public getCSVData(
		numberOfRecords: number,
		tableParams: APIParameters,
		fund?: string,
		member?: string,
		division?: string,
	): Promise<any> {
		const params: APIParameters = {
			...tableParams,
		};
		const url =
			this.getFormURLPrefix(fund, member, division) +
			'/csv-data/' +
			numberOfRecords +
			'?' +
			GenericApi.getParamsString(params, [
				ParamType.FORM,
				ParamType.DIVISION,
				ParamType.CUSTOMISATION,
			]);

		return FetchUtils.getJSONWithParamsInURL<any>(url);
	}
}
